import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { normalizeNif } from '@/lib/holded/normalizeNif';

export interface HoldedContactMatch {
  id: string;
  name: string;
  tradeName?: string;
  code: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  province: string | null;
}

export type LookupStatus = 'idle' | 'searching' | 'found' | 'not_found' | 'disabled' | 'error';

export function useHoldedContactLookup(nifRaw: string, enabled: boolean) {
  const [status, setStatus] = useState<LookupStatus>('idle');
  const [contact, setContact] = useState<HoldedContactMatch | null>(null);
  const [searchedNif, setSearchedNif] = useState<string>('');

  useEffect(() => {
    if (!enabled) {
      setStatus('disabled');
      setContact(null);
      return;
    }
    const nif = normalizeNif(nifRaw);
    if (!nif || nif.length < 4) {
      setStatus('idle');
      setContact(null);
      return;
    }
    let cancelled = false;
    setStatus('searching');
    const t = setTimeout(async () => {
      try {
        const { data, error } = await supabase.functions.invoke('holded-lookup-contact', {
          body: { nif },
        });
        if (cancelled) return;
        if (error || !data) {
          setStatus('error');
          setContact(null);
          return;
        }
        if (!data.enabled) {
          setStatus('disabled');
          setContact(null);
          return;
        }
        setSearchedNif(nif);
        if (data.found && data.contact) {
          setContact(data.contact);
          setStatus('found');
        } else {
          setContact(null);
          setStatus('not_found');
        }
      } catch {
        if (!cancelled) {
          setStatus('error');
          setContact(null);
        }
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [nifRaw, enabled]);

  const reset = () => {
    setStatus('idle');
    setContact(null);
  };

  return { status, contact, searchedNif, reset };
}
